import {
  dataInvalidatedPayloadSchema,
  modulesUpdatedPayloadSchema,
  notificationNewPayloadSchema,
  RealtimeEvent,
  remoteSessionUpdatedPayloadSchema,
  type AuthUser,
} from '@gb-ticket/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { toast } from 'sonner';

import { meQueryKey, useMe } from '@/features/auth/use-me';
import { notificationsQueryKey } from '@/features/notifications/use-notifications';
import { receiveInvalidation } from '@/platform/lib/data-invalidation';
import { setRealtimeConnected } from '@/platform/lib/realtime-connection';
import { invalidateScope } from '@/platform/lib/use-invalidation-hold';
import { csatPendingQueryKey } from '@/products/helpdesk/features/csat/use-csat';
import { remoteSessionQueryKey } from '@/products/helpdesk/features/remote-support/use-remote-support';
import { systemModulesQueryKey } from '@/products/helpdesk/features/settings/use-system-modules';

export function useRealtimeNotifications() {
  const me = useMe();
  const queryClient = useQueryClient();
  const userId = me.data?.id;

  useEffect(() => {
    if (!userId) {
      return;
    }

    const socket: Socket = io(`${window.location.origin}/ws`, {
      path: '/socket.io',
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    const onConnection = () => setRealtimeConnected(socket.connected);
    socket.on('connect', onConnection);
    socket.on('disconnect', onConnection);

    socket.on(RealtimeEvent.DATA_INVALIDATED, (raw: unknown) => {
      const parsed = dataInvalidatedPayloadSchema.safeParse(raw);
      if (!parsed.success) {
        return;
      }
      const scope = receiveInvalidation(parsed.data.scope);
      if (scope) {
        invalidateScope(queryClient, scope);
      }
    });

    socket.on(RealtimeEvent.NOTIFICATION_NEW, (raw: unknown) => {
      const parsed = notificationNewPayloadSchema.safeParse(raw);
      if (!parsed.success) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
      void queryClient.invalidateQueries({ queryKey: csatPendingQueryKey });
      void queryClient.invalidateQueries({ queryKey: ['csat', 'ticket'] });
      toast(parsed.data.notification.title, {
        description: parsed.data.notification.body,
      });
    });

    socket.on(RealtimeEvent.REMOTE_SESSION_UPDATED, (raw: unknown) => {
      const parsed = remoteSessionUpdatedPayloadSchema.safeParse(raw);
      if (!parsed.success) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: remoteSessionQueryKey(String(parsed.data.session.ticketNumber)),
      });
    });

    socket.on(RealtimeEvent.MODULES_UPDATED, (raw: unknown) => {
      const parsed = modulesUpdatedPayloadSchema.safeParse(raw);
      if (!parsed.success) {
        return;
      }
      queryClient.setQueryData<AuthUser | null>(meQueryKey, (current) =>
        current ? { ...current, enabledModules: parsed.data.enabledModules } : current,
      );
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
      void queryClient.invalidateQueries({ queryKey: systemModulesQueryKey });
    });

    return () => {
      setRealtimeConnected(false);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [queryClient, userId]);
}
